import React, { FunctionComponent, useMemo, useCallback } from 'react'
import { Text, useIsCashTheme } from '@afterpaytouch/core'
import { OrderPaymentSchedule } from '@afterpaytouch/portal-api/types'
import { Trans, useTranslation } from 'next-i18next'
import { Money } from '../Money'
import { getPaymentLabel, getTextColor } from './utils'
import { useConsumerSizes } from '../../utils/responsive'
import {
  isScheduleOverdue as checkScheduleOverdue,
  isSchedulePartialRefund as checkSchedulePartialRefund,
  isSchedulePaid as checkSchedulePaid,
} from '../../state/orders/utils'
import { isValidMoney } from '../Money/utils'
import { useGetOrderTransactionQuery } from '../../state/orders/hooks'
import { PaymentType } from '@afterpay/types'

interface OrderScheduleInstallmentLabelProps {
  orderId: string
  installment: OrderPaymentSchedule
  numberOfInstallments: number
  isNextDue: boolean
}

const testNameSpace = 'order-schedule-installment-label'

const I18N_NAMESPACE = ['common', 'orders']

export const OrderScheduleInstallmentLabel: FunctionComponent<OrderScheduleInstallmentLabelProps> = ({
  orderId,
  installment,
  numberOfInstallments,
  isNextDue,
}) => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const isCashTheme = useIsCashTheme()
  const { data } = useGetOrderTransactionQuery(orderId)
  const { paymentUpFront, paymentType } = data ?? {}
  const isScheduleOverdue = useMemo(() => checkScheduleOverdue(installment), [installment])
  const isSchedulePaid = useMemo(() => checkSchedulePaid(installment), [installment])
  const isSchedulePartialRefund = useMemo(() => checkSchedulePartialRefund(installment), [installment])
  const isPCLOrder = useMemo(() => paymentType === PaymentType.PCL, [paymentType])
  const paymentLabel = getPaymentLabel({ installment, isDownPayment: Boolean(isPCLOrder && paymentUpFront), numberOfInstallments }, t)
  const { smallTextSize } = useConsumerSizes()
  const textColor = useCallback(getTextColor, [isScheduleOverdue, isSchedulePaid, isNextDue, isSchedulePartialRefund, isCashTheme])(
    isScheduleOverdue,
    isSchedulePaid,
    isNextDue,
    isSchedulePartialRefund,
    isCashTheme
  )
  return (
    <>
      <Text renderAs='span' size={smallTextSize} color={textColor} testNameSpace={`${testNameSpace}-payment`}>
        {paymentLabel}
      </Text>
      {isValidMoney(installment.orderFee) && (
        <Text renderAs='span' size={smallTextSize} testNameSpace={`${testNameSpace}-fee`}>
          <Trans
            components={{
              amount: <Money size={smallTextSize} value={installment.orderFee} />,
            }}
            i18nKey='orders:orderSchedule:lateFeesSummary'
          />
        </Text>
      )}
    </>
  )
}
